import { KeyboardArrowUp } from '@mui/icons-material'
import {
  alpha,
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  useTheme,
} from '@mui/material'
import { useFormikContext } from 'formik'
import { useMemo } from 'react'

import rem, { getWhiteOrBlackTextColorByBackground } from '~/utils/ui'

type TProps = {
  accentColor: string
  isPreChatFormVisible: boolean
  isSendDisabled: boolean
  isSubmittingPreChatForm: boolean
  onSendClick: () => void
}

function SendButton({
  accentColor,
  isPreChatFormVisible,
  isSendDisabled,
  isSubmittingPreChatForm,
  onSendClick,
}: TProps) {
  const theme = useTheme()
  const { errors } = useFormikContext<Record<string, string>>()

  const errorMessages = useMemo(
    () =>
      Object.values(errors).filter(
        (message): message is string =>
          typeof message === 'string' && message.length > 0,
      ),
    [errors],
  )

  const showValidationTooltip = isPreChatFormVisible && errorMessages.length > 0

  const iconButton = (
    <IconButton
      type="button"
      size="small"
      onClick={onSendClick}
      disabled={isSendDisabled}
      sx={{
        backgroundColor: alpha(accentColor, 0.5),
        color: getWhiteOrBlackTextColorByBackground(accentColor, theme),
        width: rem(28),
        height: rem(28),
        '&:hover': {
          backgroundColor: accentColor,
        },
      }}
    >
      {isSubmittingPreChatForm ? (
        <CircularProgress size={rem(16)} sx={{ color: 'inherit' }} />
      ) : (
        <KeyboardArrowUp sx={{ fontSize: rem(20) }} />
      )}
    </IconButton>
  )

  if (!showValidationTooltip) {
    return iconButton
  }

  return (
    <Tooltip
      title={
        <Box component="ul" sx={{ margin: 0, paddingLeft: rem(16) }}>
          {errorMessages.map((message, index) => (
            <Box component="li" key={`${message}-${index.toString()}`}>
              {message}
            </Box>
          ))}
        </Box>
      }
    >
      <Box component="span" sx={{ display: 'inline-flex' }}>
        {iconButton}
      </Box>
    </Tooltip>
  )
}

export default SendButton
